// ./src/components/features/WorkflowTable/WorkflowTable.tsx
import React, { useMemo } from "react";
import { Text, Group, Stack } from "@mantine/core";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
} from "@/components/generic";
import { Workflow } from "@/utils/api";
import { formatDateTime } from "@/utils/dateHelpers";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface WorkflowTableProps {
  /** Workflows to display */
  workflows: Workflow[];

  /** True while the initial page is loading */
  loading: boolean;

  /** True while an additional page is being appended (infinite scroll) */
  loadingMore: boolean;

  /** Whether more pages are available */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when the user changes the sort order */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback when the user scrolls to the bottom of the table */
  onLoadMore: () => void;

  /** Callback when the Edit action is clicked */
  onEditWorkflow?: (workflow: Workflow) => void;

  /**
   * Callback when the Duplicate action is clicked.
   * The table passes the full source workflow so the caller can pre-populate
   * the create form (rename, then save — no immediate API call here).
   */
  onDuplicateWorkflow?: (workflow: Workflow) => void;

  /** Callback when the Remove action is clicked */
  onRemoveWorkflow?: (workflow: Workflow) => void;

  /** Callback when the Go In (manage items) action is clicked */
  onGoInWorkflow?: (workflow: Workflow) => void;

  /**
   * Set of IDs that are currently being deleted.
   * Each id in the set causes its row's remove button to show a spinner.
   * Multiple ids can be present simultaneously (concurrent removals).
   */
  removingIds?: Set<string>;

  /**
   * ID of the workflow that was most recently created or updated.
   * That row is highlighted until the list is reloaded or the highlight
   * is explicitly cleared by the parent.
   */
  lastEditedId?: string | null;

  /** Table density */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const WorkflowTable: React.FC<WorkflowTableProps> = ({
  workflows,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditWorkflow,
  onDuplicateWorkflow,
  onRemoveWorkflow,
  onGoInWorkflow,
  removingIds,
  lastEditedId,
  size = "md",
}) => {
  const columns: AppTableColumn<Workflow>[] = useMemo(
    () => [
      // ----------------------------------------------------------------
      // Name + description — flex column, absorbs available width
      // ----------------------------------------------------------------
      {
        key: "workflowName",
        label: "Name",
        sortable: true,
        flex: true,
        minWidth: 220,
        render: (value, record) => (
          <Stack gap={2}>
            <Text fw={500} size="sm" lineClamp={1}>
              {value || "Unnamed workflow"}
            </Text>
            {record.description && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {record.description}
              </Text>
            )}
          </Stack>
        ),
      },

      // ----------------------------------------------------------------
      // Status
      // ----------------------------------------------------------------
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 110,
        minWidth: 90,
        align: "left",
        render: (value) => <StatusBadge status={value} />,
      },

      // ----------------------------------------------------------------
      // Created date
      // ----------------------------------------------------------------
      {
        key: "createdAt",
        label: "Created",
        sortable: true,
        width: 150,
        minWidth: 130,
        align: "left",
        render: (value) => (
          <Text
            size="sm"
            c="dimmed"
            dangerouslySetInnerHTML={{
              __html: formatDateTime(value, "datetime2"),
            }}
          />
        ),
      },

      // ----------------------------------------------------------------
      // Actions — 180px to comfortably fit edit, duplicate, go-in, remove
      // ----------------------------------------------------------------
      {
        key: "actions",
        label: "",
        width: 180,
        minWidth: 180,
        align: "right",
        render: (_, record: Workflow) => {
          // A row is "removing" if its id appears in the set.
          // The set may contain many ids simultaneously (concurrent removals).
          const isRemoving =
            !!record.id && (removingIds?.has(record.id) ?? false);

          return (
            <Group gap="xs" wrap="nowrap" justify="flex-end">
              <RecordActions
                context={record}
                onEditClick={onEditWorkflow}
                onDuplicateClick={onDuplicateWorkflow}
                onGoInClick={onGoInWorkflow}
                goInTooltip="Manage workflow items"
                onRemoveClick={onRemoveWorkflow}
                removeInProgress={isRemoving}
              />
            </Group>
          );
        },
      },
    ],
    // Re-create columns only when the callback refs or the removingIds Set
    // reference changes. The Set reference changes whenever any id is added
    // or removed (the hook always produces a new Set instance), which is
    // exactly when we need the column to re-render the affected rows.
    [
      onEditWorkflow,
      onDuplicateWorkflow,
      onGoInWorkflow,
      onRemoveWorkflow,
      removingIds,
    ],
  );

  return (
    <AppTable
      data={workflows}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(workflow) => workflow?.id ?? ""}
      highlightRow={(workflow) =>
        !!lastEditedId && workflow?.id === lastEditedId
      }
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
